import { type RefObject, useCallback } from 'react'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { HTTP_RESPONSE_STATUS } from 'common/constants/httpMethodsConstants'
import { useAppDispatch } from 'common/hooks/redux'

import {
  CHIEF_COMPLAINTS_TAG,
  chiefComplaintsApi,
  useReplaceAppointmentChiefComplaintsMutation,
} from 'features/Schedule/api/chiefComplaints.api'
import { getErrorStatus } from 'features/Schedule/api/chiefComplaints.transform'
import { getIssueErrorMessage } from 'features/Schedule/api/medicalIssues.transform'

import type { ComplaintListSnapshot } from './complaintField.types'

const NOTIFICATION_TITLE = 'Chief complaints'
const SAVE_FAILED = 'Chief complaints could not be saved.'

// Only a 4xx carries words meant for the user; the rest hold axios' own message.
const isClientError = (status: number | null): boolean =>
  status !== null &&
  status >= HTTP_RESPONSE_STATUS.BAD_REQUEST &&
  status < HTTP_RESPONSE_STATUS.INTERNAL_SERVER_ERROR

type Args = {
  siteId?: string
  listRef: RefObject<ComplaintListSnapshot>
}

// Runs before the booking update; false means the dialog must stay open.
export const useSaveAppointmentComplaints = ({ siteId, listRef }: Args) => {
  const dispatch = useAppDispatch()
  const { setNotification } = useNotification()
  const [replaceComplaints] = useReplaceAppointmentChiefComplaintsMutation()

  return useCallback(
    async (appointmentId?: number | null): Promise<boolean> => {
      const list = listRef.current
      if (!appointmentId || !siteId || !list.dirty || list.frozen) return true

      try {
        await replaceComplaints({
          appointmentId,
          siteId,
          data: { chief_complaints: list.rows },
        }).unwrap()
        return true
      } catch (error) {
        const status = getErrorStatus(error)
        setNotification({
          title: NOTIFICATION_TITLE,
          description: isClientError(status)
            ? getIssueErrorMessage(error, SAVE_FAILED)
            : SAVE_FAILED,
          type: ALERT_CONSTANTS.ERROR,
        })
        // The 409 text carries no reason field; the reloaded list does.
        if (status === HTTP_RESPONSE_STATUS.CONFLICT) {
          dispatch(
            chiefComplaintsApi.util.invalidateTags([
              { type: CHIEF_COMPLAINTS_TAG, id: appointmentId },
            ]),
          )
        }
        return false
      }
    },
    [dispatch, listRef, replaceComplaints, setNotification, siteId],
  )
}
